/**
 * DecisionFlow — TerritoryManager
 * ui/src/components/tenant-admin/TerritoryManager.jsx
 *
 * Domain: §P4-01 Territory
 * Nav route: /admin/territory
 * API:
 *   GET  /api/v1/territory-assignments             → list (date-overlap filtered)
 *   POST /api/v1/territory-assignments             → create
 *   POST /api/v1/territory-assignments/{id}/approve
 *   GET  /api/v1/territory-quotas?fiscal_period_id={id}
 *   POST /api/v1/territory-quotas
 *   POST /api/v1/territory-quotas/{id}/approve
 * Feature gate: Enterprise — territory_compensation
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner, no content rendered.
 * Tabs: Assignments | Quotas
 * Notes: territory_assignment has no fiscal_period_id — date-overlap filter instead.
 * State derived from approved_by / submitted_at — no status column.
 * Path: ui/src/components/tenant-admin/TerritoryManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'assignments', label: 'Assignments' },
  { key: 'quotas',      label: 'Quotas'      },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_ASSIGNMENT = {
  territory_id:    '',
  position_id:     '',
  assignment_type: 'primary',
  start_date:      '',
  end_date:        '',
};

const EMPTY_QUOTA = {
  territory_id: '',
  quota_amount: '',
  currency:     'USD',
};

const ASSIGNMENT_TYPES = [
  { value: 'primary',   label: 'Primary'   },
  { value: 'secondary', label: 'Secondary' },
  { value: 'overlay',   label: 'Overlay'   },
];

// ---------------------------------------------------------------------------
// TerritoryManager
// ---------------------------------------------------------------------------

export default function TerritoryManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.territory')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [gated, setGated]                   = useState(false);
  const [activeTab, setActiveTab]           = useState('assignments');

  // Assignments tab
  const [assignments, setAssignments]       = useState([]);
  const [assignLoading, setAssignLoading]   = useState(false);
  const [assignError, setAssignError]       = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm]         = useState(EMPTY_ASSIGNMENT);
  const [assignSaving, setAssignSaving]     = useState(false);
  const [assignFormError, setAssignFormError] = useState('');
  const [assignActionLoading, setAssignActionLoading] = useState(false);
  const [assignActionError, setAssignActionError]     = useState('');

  // Quotas tab
  const [quotas, setQuotas]                 = useState([]);
  const [quotaLoading, setQuotaLoading]     = useState(false);
  const [quotaError, setQuotaError]         = useState('');
  const [selectedQuota, setSelectedQuota]   = useState(null);
  const [showQuotaForm, setShowQuotaForm]   = useState(false);
  const [quotaForm, setQuotaForm]           = useState(EMPTY_QUOTA);
  const [quotaSaving, setQuotaSaving]       = useState(false);
  const [quotaFormError, setQuotaFormError] = useState('');
  const [quotaActionLoading, setQuotaActionLoading] = useState(false);
  const [quotaActionError, setQuotaActionError]     = useState('');

  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load assignments — enterprise gate check first
  // ---------------------------------------------------------------------------

  const loadAssignments = useCallback(async () => {
    if (!user) return;
    setAssignLoading(true);
    setAssignError('');
    setGated(false);
    try {
      // No fiscal_period_id on territory_assignment — date filter handled server-side
      const data = await apiGet('/api/v1/territory-assignments');
      setAssignments(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') {
        setGated(true);
      } else {
        setAssignError(getErrorMessage(error, 'Unable to load territory assignments.'));
      }
    } finally {
      setAssignLoading(false);
    }
  }, [user]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // ---------------------------------------------------------------------------
  // Load quotas
  // ---------------------------------------------------------------------------

  const loadQuotas = useCallback(async () => {
    if (!user || !fpId) return;
    setQuotaLoading(true);
    setQuotaError('');
    try {
      const data = await apiGet(`/api/v1/territory-quotas?fiscal_period_id=${fpId}`);
      setQuotas(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setQuotaError(getErrorMessage(error, 'Unable to load territory quotas.'));
    } finally {
      setQuotaLoading(false);
    }
  }, [user, fpId]);

  useEffect(() => {
    if (activeTab === 'quotas') loadQuotas();
  }, [activeTab, loadQuotas]);

  // ---------------------------------------------------------------------------
  // Assignment actions
  // ---------------------------------------------------------------------------

  async function handleCreateAssignment(e) {
    e.preventDefault();
    if (!assignForm.territory_id.trim() || !assignForm.position_id.trim()) {
      setAssignFormError('Territory ID and position ID are required.');
      return;
    }
    setAssignSaving(true);
    setAssignFormError('');
    try {
      await apiPost('/api/v1/territory-assignments', {
        territory_id:    assignForm.territory_id.trim(),
        position_id:     assignForm.position_id.trim(),
        assignment_type: assignForm.assignment_type,
        start_date:      assignForm.start_date || null,
        end_date:        assignForm.end_date || null,
      });
      setShowAssignForm(false);
      setAssignForm(EMPTY_ASSIGNMENT);
      showToast('Territory assignment created.');
      await loadAssignments();
    } catch (error) {
      setAssignFormError(getErrorMessage(error, 'Failed to create assignment.'));
    } finally {
      setAssignSaving(false);
    }
  }

  async function handleAssignmentSubmit() {
    if (!selectedAssignment) return;
    setAssignActionLoading(true);
    setAssignActionError('');
    try {
      await apiPost(`/api/v1/territory-assignments/${selectedAssignment.territory_assignment_id}/submit`);
      showToast('Submitted for approval.');
      await loadAssignments();
      setSelectedAssignment((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setAssignActionError(getErrorMessage(error, 'Failed to submit.'));
    } finally {
      setAssignActionLoading(false);
    }
  }

  async function handleAssignmentApprove() {
    if (!selectedAssignment) return;
    setAssignActionLoading(true);
    setAssignActionError('');
    try {
      await apiPost(`/api/v1/territory-assignments/${selectedAssignment.territory_assignment_id}/approve`);
      showToast('Assignment approved.');
      await loadAssignments();
      setSelectedAssignment((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setAssignActionError('Self-approval is not permitted (NN-10).');
      } else {
        setAssignActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setAssignActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Quota actions
  // ---------------------------------------------------------------------------

  async function handleCreateQuota(e) {
    e.preventDefault();
    if (!quotaForm.territory_id.trim() || !quotaForm.quota_amount) {
      setQuotaFormError('Territory ID and quota amount are required.');
      return;
    }
    setQuotaSaving(true);
    setQuotaFormError('');
    try {
      await apiPost('/api/v1/territory-quotas', {
        territory_id:      quotaForm.territory_id.trim(),
        fiscal_period_id:  fpId,
        quota_amount:      parseFloat(quotaForm.quota_amount),
        currency:          quotaForm.currency || 'USD',
      });
      setShowQuotaForm(false);
      setQuotaForm(EMPTY_QUOTA);
      showToast('Territory quota created.');
      await loadQuotas();
    } catch (error) {
      setQuotaFormError(getErrorMessage(error, 'Failed to create quota.'));
    } finally {
      setQuotaSaving(false);
    }
  }

  async function handleQuotaSubmit() {
    if (!selectedQuota) return;
    setQuotaActionLoading(true);
    setQuotaActionError('');
    try {
      await apiPost(`/api/v1/territory-quotas/${selectedQuota.territory_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadQuotas();
      setSelectedQuota((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setQuotaActionError(getErrorMessage(error, 'Failed to submit.'));
    } finally {
      setQuotaActionLoading(false);
    }
  }

  async function handleQuotaApprove() {
    if (!selectedQuota) return;
    setQuotaActionLoading(true);
    setQuotaActionError('');
    try {
      await apiPost(`/api/v1/territory-quotas/${selectedQuota.territory_quota_id}/approve`);
      showToast('Quota approved.');
      await loadQuotas();
      setSelectedQuota((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setQuotaActionError('Self-approval is not permitted (NN-10).');
      } else {
        setQuotaActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setQuotaActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  if (gated) {
    return (
      <section className="flex flex-col gap-4">
        <GateStatusBanner feature="territory_compensation" />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">

      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Territory</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Assignments tab ── */}
      {activeTab === 'assignments' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Territory Assignments</h2>
            {!readOnly && (
              <button
                type="button"
                onClick={() => { setShowAssignForm((v) => !v); setAssignFormError(''); setAssignForm(EMPTY_ASSIGNMENT); }}
                className={btnPrimary}
              >
                {showAssignForm ? 'Cancel' : '+ Add Assignment'}
              </button>
            )}
          </div>

          {showAssignForm && (
            <form
              onSubmit={handleCreateAssignment}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
            >
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Territory Assignment</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Territory ID <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input
                    type="text"
                    value={assignForm.territory_id}
                    onChange={(e) => setAssignForm((f) => ({ ...f, territory_id: e.target.value }))}
                    placeholder="e.g. TERR-001"
                    className={inputClass}
                    required
                  />
                </label>
                <label className={labelClass}>
                  Position ID <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input
                    type="text"
                    value={assignForm.position_id}
                    onChange={(e) => setAssignForm((f) => ({ ...f, position_id: e.target.value }))}
                    placeholder="e.g. POS-0042"
                    className={inputClass}
                    required
                  />
                </label>
                <label className={labelClass}>
                  Assignment Type
                  <select
                    value={assignForm.assignment_type}
                    onChange={(e) => setAssignForm((f) => ({ ...f, assignment_type: e.target.value }))}
                    className={inputClass}
                  >
                    {ASSIGNMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>
                <div />
                <label className={labelClass}>
                  Start Date
                  <input
                    type="date"
                    value={assignForm.start_date}
                    onChange={(e) => setAssignForm((f) => ({ ...f, start_date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  End Date
                  <input
                    type="date"
                    value={assignForm.end_date}
                    onChange={(e) => setAssignForm((f) => ({ ...f, end_date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
              </div>
              {assignFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  {assignFormError}
                </div>
              )}
              <div className="flex gap-2">
                <button type="submit" disabled={assignSaving} className={btnPrimary}>
                  {assignSaving ? 'Saving…' : 'Create Assignment'}
                </button>
                <button type="button" onClick={() => { setShowAssignForm(false); setAssignFormError(''); }} className={btnSecondary}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {assignLoading && (
            <div className="animate-pulse space-y-2">
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
            </div>
          )}
          {assignError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {assignError}
            </div>
          )}
          {!assignLoading && !assignError && assignments.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No territory assignments</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Add assignments to map positions to territories.</p>
            </div>
          )}
          {assignments.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Territory</th>
                    <th className={thClass}>Position</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Start</th>
                    <th className={thClass}>End</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => (
                    <tr
                      key={a.territory_assignment_id}
                      className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedAssignment?.territory_assignment_id === a.territory_assignment_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                      onClick={() => { setSelectedAssignment(a); setAssignActionError(''); }}
                    >
                      <td className={tdClass + ' font-medium'}>{a.territory_id}</td>
                      <td className={tdClass}>{a.position_id}</td>
                      <td className={tdClass}>{a.assignment_type ?? '—'}</td>
                      <td className={tdClass}>{a.start_date ?? '—'}</td>
                      <td className={tdClass}>{a.end_date ?? '—'}</td>
                      <td className={tdClass}><StatusPill status={deriveState(a)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedAssignment && !readOnly && (() => {
            const state = deriveState(selectedAssignment);
            return (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Selected: Territory <strong className="text-[rgb(var(--color-text))]">{selectedAssignment.territory_id}</strong> → Position <strong className="text-[rgb(var(--color-text))]">{selectedAssignment.position_id}</strong>
                </div>
                {assignActionError && (
                  <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                    {assignActionError}
                  </div>
                )}
                <ApprovalPanel
                  variant="territory_assignment"
                  currentState={state}
                  submittedBy={selectedAssignment.submitted_by}
                  currentUserId={currentUserId}
                  onSubmit={state === 'draft' ? handleAssignmentSubmit : null}
                  onApprove={state === 'pending_approval' ? handleAssignmentApprove : null}
                  onReject={null}
                  approvedBy={selectedAssignment.approved_by}
                  approvedAt={selectedAssignment.approved_at}
                  loading={assignActionLoading}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Quotas tab ── */}
      {activeTab === 'quotas' && (
        <div className="flex flex-col gap-4">
          {!fpId && (
            <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              No active fiscal period — territory quotas require a period.
            </div>
          )}

          {fpId && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Territory Quotas</h2>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => { setShowQuotaForm((v) => !v); setQuotaFormError(''); setQuotaForm(EMPTY_QUOTA); }}
                    className={btnPrimary}
                  >
                    {showQuotaForm ? 'Cancel' : '+ Add Quota'}
                  </button>
                )}
              </div>

              {showQuotaForm && (
                <form
                  onSubmit={handleCreateQuota}
                  className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
                >
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Territory Quota</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className={labelClass}>
                      Territory ID <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input
                        type="text"
                        value={quotaForm.territory_id}
                        onChange={(e) => setQuotaForm((f) => ({ ...f, territory_id: e.target.value }))}
                        placeholder="e.g. TERR-001"
                        className={inputClass}
                        required
                      />
                    </label>
                    <label className={labelClass}>
                      Quota Amount <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={quotaForm.quota_amount}
                        onChange={(e) => setQuotaForm((f) => ({ ...f, quota_amount: e.target.value }))}
                        placeholder="0.00"
                        className={inputClass}
                        required
                      />
                    </label>
                    <label className={labelClass}>
                      Currency
                      <input
                        type="text"
                        maxLength={3}
                        value={quotaForm.currency}
                        onChange={(e) => setQuotaForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                        placeholder="USD"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  {quotaFormError && (
                    <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                      {quotaFormError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button type="submit" disabled={quotaSaving} className={btnPrimary}>
                      {quotaSaving ? 'Saving…' : 'Create Quota'}
                    </button>
                    <button type="button" onClick={() => { setShowQuotaForm(false); setQuotaFormError(''); }} className={btnSecondary}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {quotaLoading && (
                <div className="animate-pulse space-y-2">
                  <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
                </div>
              )}
              {quotaError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {quotaError}
                </div>
              )}
              {!quotaLoading && !quotaError && quotas.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No territory quotas</p>
                  <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Add quotas to set revenue targets per territory.</p>
                </div>
              )}
              {quotas.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Territory</th>
                        <th className={thClass}>Quota Amount</th>
                        <th className={thClass}>Currency</th>
                        <th className={thClass}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotas.map((q) => (
                        <tr
                          key={q.territory_quota_id}
                          className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedQuota?.territory_quota_id === q.territory_quota_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                          onClick={() => { setSelectedQuota(q); setQuotaActionError(''); }}
                        >
                          <td className={tdClass + ' font-medium'}>{q.territory_id}</td>
                          <td className={tdClass}>{q.quota_amount != null ? Number(q.quota_amount).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{q.currency ?? '—'}</td>
                          <td className={tdClass}><StatusPill status={deriveState(q)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedQuota && !readOnly && (() => {
                const state = deriveState(selectedQuota);
                return (
                  <div className="flex flex-col gap-3">
                    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                      Selected: Territory <strong className="text-[rgb(var(--color-text))]">{selectedQuota.territory_id}</strong>
                    </div>
                    {quotaActionError && (
                      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                        {quotaActionError}
                      </div>
                    )}
                    <ApprovalPanel
                      variant="territory_quota"
                      currentState={state}
                      submittedBy={selectedQuota.submitted_by}
                      currentUserId={currentUserId}
                      onSubmit={state === 'draft' ? handleQuotaSubmit : null}
                      onApprove={state === 'pending_approval' ? handleQuotaApprove : null}
                      onReject={null}
                      approvedBy={selectedQuota.approved_by}
                      approvedAt={selectedQuota.approved_at}
                      loading={quotaActionLoading}
                    />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
